'use client';

import { useAuthenticated } from '@/hooks/useAuthenticated';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/slices/store';
import { authenticate, logout } from '@/slices/features/tokenSet/tokenSetSlice';
import { getSourcePath } from '@/rewrites';
import { useEffect, useState } from 'react';
import HeaderNavigation, { HeaderNavigationProps } from '@/components/common/HeaderNavigation';
import { useRouter } from 'next/navigation';
import {
  faCalendarDays,
  faMagnifyingGlass,
  faMagnifyingGlassArrowsRotate,
  faMoneyCheckPen,
  faRightFromBracket,
  faStar,
  faUser,
  faUsers,
} from '@fortawesome/pro-duotone-svg-icons';
import { useAccount } from '@/hooks/useAccount';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { useTranslations } from 'next-intl';
import NotificationsDropdown from '@/components/notifications/NotificationsDropdown';
import Logo from '@/components/common/Logo';

const Header = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useAuthenticated();
  const { currentTier } = useAccount();
  const isBasicTier = currentTier === SubscriptionTier.BASIC;
  const { person } = useAccount();
  const [navigationBarOptions, setNavigationBarOptions] = useState<HeaderNavigationProps>();
  const router = useRouter();
  const t = useTranslations();

  const handleLoginClick = (e: any) => {
    e.preventDefault();
    dispatch(authenticate());
  };

  const handleDemoClick = () => {
    window.Calendly.initPopupWidget({ url: 'https://calendly.com/d/cr8y-5pt-7bv?hide_gdpr_banner=1' });
    return false;
  };

  const anonymousNavigation: HeaderNavigationProps = {
    leftElements: [
      { label: t('Navigation_Price'), link: getSourcePath('/pricing') },
      { label: t('Navigation_Blog'), link: getSourcePath('/blog') },
    ],
    rightElements: [
      { label: t('Navigation_Demo'), handleClick: handleDemoClick, icon: faCalendarDays, hideIconMobile: true },
      { label: t('Navigation_Login'), handleClick: handleLoginClick, showSeparator: true },
      {
        label: t('Navigation_Register'),
        link: getSourcePath('/registration'),
        className: 'text-primary-content bg-primary',
        target: '_blank',
      },
    ],
  };

  const authenticatedNavigation: HeaderNavigationProps = {
    leftElements: [
      {
        label: t('Navigation_Search'),
        link: getSourcePath('/search'),
        icon: faMagnifyingGlass,
      },
      {
        label: t('Navigation_Agents'),
        link: getSourcePath('/search-results'),
        icon: faMagnifyingGlassArrowsRotate,
        accessiblePaths: [getSourcePath('/agents')],
      },
      {
        label: t('Navigation_Favorites'),
        link: getSourcePath('/favorites'),
        icon: faStar,
      },
    ],
    rightElements: [],
  };

  const handleLogout = (e: any) => {
    e.preventDefault();
    router.push('/');
    dispatch(logout());
  };

  useEffect(() => {
    if (isAuthenticated) {
      const userInitials = person ? `${person.firstname[0]}${person.lastname[0]}`.toUpperCase() : '';
      const authNav = { ...authenticatedNavigation };

      // Only add the account menu when we have user data
      if (userInitials) {
        if (isBasicTier) {
          authNav.rightElements?.push({
            label: t('Navigation_Demo'),
            handleClick: handleDemoClick,
            icon: faCalendarDays,
          });

          authNav.rightElements?.push({
            label: 'Notification',
            component: <NotificationsDropdown />,
            showSeparator: true,
          });
        } else {
          authNav.rightElements?.push({
            label: 'Notification',
            component: <NotificationsDropdown />,
            showSeparator: false,
          });
        }
        authNav.rightElements?.push({
          label: userInitials,
          menuItem: [
            { label: t('Navigation_Profile'), link: getSourcePath('/profile'), icon: faUser },
            { label: t('Navigation_Organization'), link: getSourcePath('/organization'), icon: faUsers },
            { label: t('Navigation_Subscription'), link: getSourcePath('/subscription'), icon: faMoneyCheckPen },
            { label: t('Navigation_Logout'), handleClick: handleLogout, icon: faRightFromBracket },
          ],
        });
      }
      setNavigationBarOptions(authNav);
    } else {
      setNavigationBarOptions(anonymousNavigation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, person, currentTier]);

  return (
    <header className={`sticky top-0 z-50 flex bg-base-100 py-2 shadow-lg`}>
      <div className="page-layout flex w-full items-center justify-between gap-2">
        <Logo />
        <div className="flex w-full">
          <HeaderNavigation {...navigationBarOptions} />
        </div>
      </div>
    </header>
  );
};

export default Header;
